import {
  Button,
  Checkbox,
  Flex,
  Grid,
  InputGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useStageTeams } from '@/services/queries/stage';
import { SelectField } from '@/components/SelectField';
import { InputField } from '@/components/InputField';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { MatchScheduleFormat, SeasonType, StageType } from '@/model';
import { GetParamsObject } from '@/model/query-params';
import { useGenerateMatchesV2Mutation } from '@/services/queries/match_v2';
import { useChampionships } from '@/services/queries/championship';
import { useSeasons } from '@/services/queries/season';
import { useStages } from '@/services/queries/stage';
import { useSeasonTeams } from '@/services/queries/season-team';
import { PostMatchesGenerateRequest } from '@/services/request/match_v2';
import { modalFormSchema, ModalFormData } from './schemas';

const I18N_PREFIX = 'pages.application_championship_matches.components.match_generate_modal';

export interface MatchGenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDone: () => void;
}

const DEFAULT_FORM: ModalFormData = {
  championship_uuid: '',
  season_uuid: '',
  stage_uuid: '',
  schedule_format: MatchScheduleFormat.SINGLE_LEG,
  start_date: '',
  interval_days: 7,
  replace_existing: false,
};

export const MatchGenerateModal = ({ isOpen, onClose, onDone }: MatchGenerateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const generateMatches = useGenerateMatchesV2Mutation();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<ModalFormData>({
    defaultValues: DEFAULT_FORM,
    resolver: zodResolver(modalFormSchema),
  });

  const championshipUuid = watch('championship_uuid');
  const seasonUuid = watch('season_uuid');
  const stageUuid = watch('stage_uuid');
  const scheduleFormat = watch('schedule_format');

  const championshipsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'),
    [],
  );
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      championshipUuid
        ? GetParamsObject(
            { championship_uuid: championshipUuid } as Record<string, string>,
            1,
            200,
            'desc',
            'year',
          )
        : undefined,
    [championshipUuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  const stagesParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject(
            { season_uuid: seasonUuid } as Record<string, string>,
            1,
            200,
            'asc',
            'stage_order',
          )
        : undefined,
    [seasonUuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  const selectedStage = useMemo(
    () => (stagesData?.data ?? []).find(s => s.uuid === stageUuid),
    [stagesData, stageUuid],
  );

  const isLeagueStage = selectedStage?.type === StageType.LEAGUE;
  const isGroupStage = selectedStage?.type === StageType.GROUP;

  const seasonTeamsParams = useMemo(
    () =>
      seasonUuid
        ? GetParamsObject({ season_uuid: seasonUuid } as Record<string, string>, 1, 200)
        : undefined,
    [seasonUuid],
  );
  const { data: seasonTeamsData, isFetching: seasonTeamsFetching } = useSeasonTeams(
    seasonTeamsParams,
    !!seasonUuid && isLeagueStage,
  );

  const stageTeamsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200),
    [],
  );
  const { data: stageTeamsData, isFetching: stageTeamsFetching } = useStageTeams(
    isGroupStage ? selectedStage?.uuid ?? '' : '',
    stageTeamsParams,
  );

  const teamCount = useMemo(() => {
    if (isLeagueStage) return seasonTeamsData?.meta.total ?? 0;
    if (isGroupStage) return stageTeamsData?.meta.total ?? 0;
    return 0;
  }, [isLeagueStage, isGroupStage, seasonTeamsData, stageTeamsData]);

  const teamsLoading = (isLeagueStage && seasonTeamsFetching) || (isGroupStage && stageTeamsFetching);
  const isOddTeams = teamCount > 0 && teamCount % 2 !== 0;

  const { roundsCount, matchesCount } = useMemo(() => {
    if (teamCount < 2 || isOddTeams) return { roundsCount: 0, matchesCount: 0 };
    let rCount = teamCount - 1;
    if (scheduleFormat === MatchScheduleFormat.DOUBLE_LEG) rCount *= 2;
    const mCount = (teamCount / 2) * rCount;
    return { roundsCount: rCount, matchesCount: mCount };
  }, [teamCount, isOddTeams, scheduleFormat]);

  const previewText = useMemo(() => {
    if (!selectedStage) return t(`${I18N_PREFIX}.preview_placeholder`);
    if (teamsLoading) return t(`${I18N_PREFIX}.preview_loading`);
    if (teamCount < 2) return t(`${I18N_PREFIX}.preview_not_enough_teams`);
    if (isOddTeams) {
      return t(`${I18N_PREFIX}.preview_odd_teams`, { count: teamCount });
    }

    const stageName = selectedStage.group_name
      ? `${selectedStage.name} · ${selectedStage.group_name}`
      : selectedStage.name;
    const formatLabel =
      scheduleFormat === MatchScheduleFormat.SINGLE_LEG
        ? t(`${I18N_PREFIX}.format_single_label`)
        : t(`${I18N_PREFIX}.format_double_label`);

    return t(`${I18N_PREFIX}.preview_summary`, {
      stageName,
      teamCount,
      formatLabel,
      matchesCount,
      roundsCount,
    });
  }, [selectedStage, teamsLoading, teamCount, isOddTeams, scheduleFormat, matchesCount, roundsCount, t]);

  const handleChampionshipChange = (value: string) => {
    setValue('championship_uuid', value);
    setValue('season_uuid', '');
    setValue('stage_uuid', '');
  };

  const handleSeasonChange = (value: string) => {
    setValue('season_uuid', value);
    setValue('stage_uuid', '');
  };

  const handleClose = () => {
    reset(DEFAULT_FORM);
    onClose();
  };

  const onSubmit = handleSubmit(data => {
    const payload: PostMatchesGenerateRequest = {
      stage_uuid: data.stage_uuid,
      schedule_format: data.schedule_format,
      start_date: data.start_date,
      interval_days: data.interval_days,
      replace_existing: data.replace_existing,
    };

    generateMatches.mutate(payload, {
      onSuccess: response => {
        toast({
          status: 'success',
          title: t(`${I18N_PREFIX}.toasts.create_success`, { count: response.meta.matches_created }),
        });
        queryClient.invalidateQueries({ queryKey: ['GET_MATCHES_V2'] });
        reset(DEFAULT_FORM);
        onDone();
      },
      onError: (error: unknown) => {
        toast({
          status: 'error',
          title: (error as Error)?.message || t(`${I18N_PREFIX}.toasts.create_error`),
        });
      },
    });
  });

  const canSubmit = !!stageUuid && !teamsLoading && teamCount >= 2 && !isOddTeams;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='2xl' isCentered scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align='center' gap={2}>
            <ChakraFeatherIcon name='zap' color='brand.500' />
            <Text fontSize='medium' fontWeight='bold'>
              {t(`${I18N_PREFIX}.title`)}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex direction='column' gap={4}>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_championship`)}</Text>
                <SelectField
                  value={championshipUuid}
                  onChange={event => handleChampionshipChange(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(championshipsData?.data ?? []).map(champ => (
                    <option key={champ.uuid} value={champ.uuid}>
                      {champ.name}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_season`)}</Text>
                <SelectField
                  value={seasonUuid}
                  isDisabled={!championshipUuid}
                  onChange={event => handleSeasonChange(event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(seasonsData?.data ?? []).map(season => (
                    <option key={season.uuid} value={season.uuid}>
                      {season.year}
                      {season.type === SeasonType.HYBRID ? ` · Híbrido` : ''}
                      {season.type === SeasonType.KNOCKOUT ? ` · Mata-mata` : ''}
                      {season.type === SeasonType.LEAGUE ? ` · Liga` : ''}
                    </option>
                  ))}
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_stage`)}</Text>
                <SelectField
                  value={stageUuid}
                  isDisabled={!seasonUuid}
                  onChange={event => setValue('stage_uuid', event.target.value)}
                >
                  <option value=''>{t(`${I18N_PREFIX}.placeholder_select`)}</option>
                  {(stagesData?.data ?? [])
                    .filter(stage => stage.type !== StageType.KNOCKOUT)
                    .map(stage => (
                      <option key={stage.uuid} value={stage.uuid}>
                        {stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name}
                      </option>
                    ))}
                </SelectField>
                {errors.stage_uuid?.message && (
                  <Text color='red.500' fontSize='sm' mt={1}>
                    {errors.stage_uuid.message}
                  </Text>
                )}
              </InputGroup>
            </Grid>

            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_format`)}</Text>
                <SelectField {...register('schedule_format')} isDisabled={!stageUuid}>
                  <option value={MatchScheduleFormat.SINGLE_LEG}>
                    {t(`${I18N_PREFIX}.format_single`)}
                  </option>
                  <option value={MatchScheduleFormat.DOUBLE_LEG}>
                    {t(`${I18N_PREFIX}.format_double`)}
                  </option>
                </SelectField>
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_start_date`)}</Text>
                <InputField
                  type='date'
                  fullDatePickerArea
                  variant='outline'
                  {...register('start_date')}
                  isDisabled={!stageUuid}
                />
                {errors.start_date?.message && (
                  <Text color='red.500' fontSize='sm' mt={1}>
                    {errors.start_date.message}
                  </Text>
                )}
              </InputGroup>

              <InputGroup display='flex' flexDirection='column'>
                <Text fontWeight='medium'>{t(`${I18N_PREFIX}.field_interval`)}</Text>
                <InputField
                  type='number'
                  variant='outline'
                  {...register('interval_days')}
                  isDisabled={!stageUuid}
                  min={1}
                />
                {errors.interval_days?.message && (
                  <Text color='red.500' fontSize='sm' mt={1}>
                    {errors.interval_days.message}
                  </Text>
                )}
              </InputGroup>
            </Grid>

            <Controller
              name='replace_existing'
              control={control}
              render={({ field }) => (
                <Checkbox isChecked={!!field.value} onChange={field.onChange} mt={2}>
                  {t(`${I18N_PREFIX}.field_replace_existing`)}
                </Checkbox>
              )}
            />

            <Flex
              mt={4}
              p={4}
              bg={isOddTeams ? 'red.50' : 'gray.50'}
              border='1px solid'
              borderColor={isOddTeams ? 'red.200' : 'gray.200'}
              borderRadius='md'
              align='center'
              justify='center'
            >
              <Text
                fontSize='md'
                color={isOddTeams ? 'red.600' : 'gray.600'}
                textAlign='center'
                fontWeight='medium'
              >
                {previewText}
              </Text>
            </Flex>
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex w='100%' justifyContent='flex-end' gap={3}>
            <Button
              variant='outline'
              color='gray.600'
              onClick={handleClose}
              isDisabled={generateMatches.isPending}
            >
              {t(`${I18N_PREFIX}.cancel_button`)}
            </Button>
            <Button
              bg='brand.500'
              color='white'
              _hover={{ bg: 'brand.600' }}
              px={10}
              onClick={onSubmit}
              isLoading={generateMatches.isPending}
              isDisabled={!canSubmit}
            >
              {canSubmit
                ? t(`${I18N_PREFIX}.confirm_button_matches`, { count: matchesCount })
                : t(`${I18N_PREFIX}.confirm_button`)}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
